import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types';
import { UserService } from '../../services/userService';
import { useSnackbar } from '../../hooks/useSnackbar';
import { SearchBar, DataTable, ActionButton, ActionMenu, EmptyTableRow, DetailSearchModal, ConfirmDialog, UserTableRow } from './common';

export const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; user: User } | null>(null);
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();

  // 検索キーワード
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 並び替えの状態
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // 詳細検索
  const [detailSearchOpen, setDetailSearchOpen] = useState(false);
  const [detailSearchKeyword, setDetailSearchKeyword] = useState<string>('');
  const [detailSearchStartDate, setDetailSearchStartDate] = useState<Date | null>(null);
  const [detailSearchEndDate, setDetailSearchEndDate] = useState<Date | null>(null);
  const [detailSearchRole, setDetailSearchRole] = useState<string>('all');
  
  // 実際にフィルタリングに使用する値
  const [appliedDetailSearchKeyword, setAppliedDetailSearchKeyword] = useState<string>('');
  const [appliedDetailSearchStartDate, setAppliedDetailSearchStartDate] = useState<Date | null>(null);
  const [appliedDetailSearchEndDate, setAppliedDetailSearchEndDate] = useState<Date | null>(null);
  const [appliedDetailSearchRole, setAppliedDetailSearchRole] = useState<string>('all');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await UserService.getUsers();
      setUsers(usersData);
    } catch (err: any) {
      console.error('ユーザー取得エラー:', err);
      const errorMessage = err.code === 'permission-denied' 
        ? '権限が不足しています。Firestoreのセキュリティルールを確認してください。'
        : err.message || 'ユーザーの取得に失敗しました';
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    navigate('/admin/users/create');
  };

  const handleEditUser = (user: User) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenuAnchor({ element: event.currentTarget, user });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuEdit = (user: User) => {
    handleEditUser(user);
    handleMenuClose();
  };

  const handleMenuDelete = (user: User) => {
    handleDeleteUser(user);
    handleMenuClose();
  };


  const confirmDeleteUser = async () => {
    if (selectedUser) {
      try {
        setActionLoading(true);
        await UserService.deleteUser(selectedUser.id);
        showSnackbar('ユーザーを削除しました', 'success');
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        await fetchUsers();
      } catch (err) {
        console.error('ユーザーの削除に失敗しました:', err);
        showSnackbar('ユーザーの削除に失敗しました', 'error');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDetailSearchOpen = () => {
    setDetailSearchOpen(true);
  };

  const handleDetailSearchClose = () => {
    setDetailSearchOpen(false);
  };

  const handleDetailSearch = () => {
    setAppliedDetailSearchKeyword(detailSearchKeyword);
    setAppliedDetailSearchStartDate(detailSearchStartDate);
    setAppliedDetailSearchEndDate(detailSearchEndDate);
    setAppliedDetailSearchRole(detailSearchRole);
    setDetailSearchOpen(false);
  };

  const handleDetailSearchReset = () => {
    setDetailSearchKeyword('');
    setDetailSearchStartDate(null);
    setDetailSearchEndDate(null);
    setDetailSearchRole('all');
    setAppliedDetailSearchKeyword('');
    setAppliedDetailSearchStartDate(null);
    setAppliedDetailSearchEndDate(null);
    setAppliedDetailSearchRole('all');
  };

  // 並び替え処理
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // フィルタリングとソート
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      if (!searchKeyword) return true;
      const keyword = searchKeyword.toLowerCase();
      return (
        user.email.toLowerCase().includes(keyword) ||
        (user.displayName && user.displayName.toLowerCase().includes(keyword))
      );
    });

    // 詳細検索：キーワード
    if (appliedDetailSearchKeyword.trim()) {
      filtered = filtered.filter(user => {
        const emailMatch = user.email?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase());
        const nameMatch = user.displayName?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase());
        return emailMatch || nameMatch;
      });
    }

    // 詳細検索：権限
    if (appliedDetailSearchRole && appliedDetailSearchRole !== 'all') {
      filtered = filtered.filter(user => user.role === appliedDetailSearchRole);
    }

    // 詳細検索：登録日
    if (appliedDetailSearchStartDate || appliedDetailSearchEndDate) {
      filtered = filtered.filter(user => {
        const dateToCheck = user.createdAt;
        
        if (!dateToCheck) return false;

        const checkDate = new Date(dateToCheck);
        checkDate.setHours(0, 0, 0, 0);

        if (appliedDetailSearchStartDate) {
          const startDate = new Date(appliedDetailSearchStartDate);
          startDate.setHours(0, 0, 0, 0);
          if (checkDate < startDate) return false;
        }

        if (appliedDetailSearchEndDate) {
          const endDate = new Date(appliedDetailSearchEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (checkDate > endDate) return false;
        }

        return true;
      });
    }

    // ソート
    filtered.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (sortField) {
        case 'displayName':
          valueA = a.displayName || '-';
          valueB = b.displayName || '-';
          break;
        case 'email':
          valueA = a.email;
          valueB = b.email;
          break;
        case 'role':
          valueA = a.role || 'viewer';
          valueB = b.role || 'viewer';
          break;
        case 'createdAt':
          valueA = a.createdAt.getTime();
          valueB = b.createdAt.getTime();
          break;
        case 'updatedAt':
          valueA = a.updatedAt.getTime();
          valueB = b.updatedAt.getTime();
          break;
        default:
          return 0;
      }
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc' 
          ? valueA.localeCompare(valueB, 'ja')
          : valueB.localeCompare(valueA, 'ja');
      }
      
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    });

    return filtered;
  }, [users, searchKeyword, sortField, sortDirection, appliedDetailSearchKeyword, appliedDetailSearchRole, appliedDetailSearchStartDate, appliedDetailSearchEndDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          再読み込み
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={4}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ fontSize: '32px', fontWeight: 600, color: '#000000' }}
          >
            ユーザー管理
          </Typography>
          <SearchBar
            value={searchKeyword}
            onChange={setSearchKeyword}
            borderRadius="16px"
            height="48px"
            fontSize="14px"
            onFilterClick={handleDetailSearchOpen}
          />
        </Box>
        <IconButton
          onClick={handleCreateUser}
          sx={{
            background: '#1B76D2 0% 0% no-repeat padding-box',
            borderRadius: '16px',
            color: '#FFFFFF',
            width: 56,
            height: 56,
            '&:hover': {
              backgroundColor: '#1565C0',
            },
          }}
        >
          <AddIcon sx={{ fontSize: '28px', fontWeight: 900, transform: 'scale(1.1)' }} />
        </IconButton>
      </Box>

      <DataTable>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      color: '#000000', 
                      fontSize: '12px', 
                      fontWeight: 500, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      pl: 4,
                    }}
                    onClick={() => handleSort('displayName')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      名前
                      {sortField === 'displayName' && (
                        sortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: '#000000', 
                      fontSize: '12px', 
                      fontWeight: 500, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    メールアドレス
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: '#000000', 
                      fontSize: '12px', 
                      fontWeight: 500, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleSort('role')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      権限
                      {sortField === 'role' && (
                        sortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: '#000000', 
                      fontSize: '12px', 
                      fontWeight: 500, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleSort('createdAt')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      登録日
                      {sortField === 'createdAt' && (
                        sortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      color: '#000000', 
                      fontSize: '12px', 
                      fontWeight: 500, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}
                    onClick={() => handleSort('updatedAt')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      最終更新日
                      {sortField === 'updatedAt' && (
                        sortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#000000', fontSize: '12px', fontWeight: 500, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', position: 'sticky', right: 0, backgroundColor: '#FFFFFF', zIndex: 10, pr: 2 }}>
                    アクション
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedUsers.length === 0 ? (
                  <EmptyTableRow colSpan={6} message="ユーザーがありません。新しいユーザーを登録してください。" />
                ) : (
                  filteredAndSortedUsers.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      onEdit={handleEditUser}
                      onMenuOpen={handleMenuOpen}
                    />
                  ))
                )}
              </TableBody>
      </DataTable>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" color="textSecondary">
          1 - {users.length} / {users.length}件の結果
        </Typography>
      </Box>

      <ActionMenu
        anchorEl={menuAnchor?.element || null}
        open={!!menuAnchor}
        onClose={handleMenuClose}
        items={[
          {
            label: '削除',
            icon: <DeleteIcon sx={{ fontSize: 18 }} />,
            onClick: () => menuAnchor && handleMenuDelete(menuAnchor.user),
            color: '#D32F2F',
          },
        ]}
      />

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDeleteUser}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />

      {/* 詳細検索モーダル */}
      <DetailSearchModal
        open={detailSearchOpen}
        onClose={handleDetailSearchClose}
        onSearch={handleDetailSearch}
        keyword={detailSearchKeyword}
        onKeywordChange={setDetailSearchKeyword}
        keywordLabel="キーワード (名前 / メールアドレス)"
        keywordPlaceholder="キーワードを入力"
        startDate={detailSearchStartDate}
        onStartDateChange={setDetailSearchStartDate}
        endDate={detailSearchEndDate}
        onEndDateChange={setDetailSearchEndDate}
        selectLabel="権限"
        selectValue={detailSearchRole}
        onSelectChange={setDetailSearchRole}
        selectOptions={[
          { value: 'all', label: 'すべて' },
          { value: 'admin', label: '管理者' },
          { value: 'viewer', label: '閲覧者' },
        ]}
      />

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

